/**
 * "Next dates" — the synchronous preview under the "Make this recurring"
 * block. Runs the TS twin of the DB's schedule rule
 * (`previewOccurrenceDates`, `@releve/shared/recurring/schedule`) on every
 * keystroke; there is no endpoint and no query. The DB stays the authority:
 * both sides are asserted against one vector file, so what this lists is
 * what the scheduler will produce.
 *
 * Template-edit mode mirrors the PATCH recompute: the first listed date is
 * `first_on_or_after(new rule, start, GREATEST(org-today, last generated + 1))`
 * — never `start_date` alone, which would re-list a date that already has a
 * document. A start the user has moved is never re-checked here; validation
 * (`recurrenceFormSchemaFor`) owns that.
 *
 * Rendered inside the section's theme-aware `bg-muted` band
 * (`docs/FULLSCREEN_FORM_MODALS.md`): the eyebrow is the micro section-head
 * recipe, the dates a two-column `text-sm font-light tabular-nums` list with
 * the weekday muted, the sentence `text-sm text-muted-foreground`. The
 * schedule sentence itself (`describeRecurrence`) is the band's headline,
 * owned by `RecurringSection`.
 */
import { useTranslation } from '@/hooks/useTranslation';
import { safeFormatDate } from '@/lib/dateUtils';
import { computePreviewDates, type RecurringNextDatesPreviewProps } from './previewDates';

export type { RecurringNextDatesPreviewProps } from './previewDates';

export function RecurringNextDatesPreview(props: RecurringNextDatesPreviewProps) {
  const { t } = useTranslation();
  const { documentType, orgToday, template } = props;

  // Cheap and pure — five date steps; no memo needed (and `props` is a fresh object per render).
  const dates = computePreviewDates(props);

  if (dates == null) return null;

  const doc = t(`recurring.words.${documentType}`, { count: 1 });
  const first = dates[0];
  // "The first … will be created …" only reads right for a schedule that has
  // never run; an edited template that already generated shows the list alone.
  const showFirstSentence = !template || template.occurrences_generated === 0;

  return (
    <div data-testid="recurring-next-dates">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
        {t('recurring.form.nextDates')}
      </h3>
      {dates.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('recurring.form.noDates')}</p>
      ) : (
        <>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1 text-sm font-light tabular-nums">
            {dates.map((iso) => (
              <li key={iso} className="flex items-baseline justify-between gap-4 sm:justify-start">
                <span>{safeFormatDate(iso, 'MMM dd, yyyy')}</span>
                <span className="text-muted-foreground">{safeFormatDate(iso, 'EEEE')}</span>
              </li>
            ))}
          </ul>
          {showFirstSentence && first && (
            <p className="text-sm text-muted-foreground mt-3">
              {first <= orgToday
                ? t('recurring.form.firstToday', { doc })
                : t('recurring.form.firstOn', { doc, date: safeFormatDate(first, 'MMM dd, yyyy') })}
            </p>
          )}
        </>
      )}
    </div>
  );
}
